/**
 * SortableListItem Component
 *
 * Individual sortable item that wraps children with drag functionality.
 * Uses useSortable hook and provides visual feedback during drag.
 *
 * Drag handle behavior:
 * - Hidden by default (doesn't take layout space)
 * - Shows on hover with slide-in animation
 * - Also visible on keyboard focus for accessibility
 */

import React, { memo } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSortableListContext } from './SortableListContext';
import { DragHandle } from './DragHandle';
import type { SortableListItemProps } from './SortableList.types';

/**
 * SortableList.Item - Wrapper for individual sortable items
 *
 * Provides:
 * - Drag-and-drop functionality via useSortable
 * - Built-in drag handle (hidden by default, shows on hover)
 * - Visual feedback during drag (opacity, scale, shadow)
 * - Theme-aware styling
 */
export const SortableListItem = memo<SortableListItemProps>(
  function SortableListItem({
    children,
    showDragHandle = true,
    dragHandlePosition = 'start',
    disabled: itemDisabled = false,
    sx,
    'data-testid': testId,
    dragHandleTestId,
  }) {
    const theme = useTheme();
    const context = useSortableListContext();

    // Get the stable ID for current item
    const id = context.currentItem ? context.getItemId(context.currentItem) : '';
    const isDisabled = itemDisabled || context.disabled;

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id,
      disabled: isDisabled,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    // Position styles for drag handle based on dragHandlePosition
    // Handle overlays inside the item - offset to leave room for item number
    const handlePositionStyles = dragHandlePosition === 'start'
      ? { left: 8, top: 4 }  // left: 8px; top: 4px to align with content
      : { right: 4 };

    return (
      <Box
        ref={setNodeRef}
        style={style}
        role="listitem"
        data-testid={testId}
        sx={{
          position: 'relative',
          // Standard drag feedback
          opacity: isDragging ? 0.6 : 1,
          transform: isDragging ? 'scale(1.03)' : 'scale(1)',
          backgroundColor: isDragging
            ? alpha(theme.palette.primary.main, 0.08)
            : 'transparent',
          boxShadow: isDragging
            ? `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`
            : 'none',
          borderRadius: theme.shape.borderRadius,
          transition: theme.transitions.create(
            ['transform', 'opacity', 'background-color', 'box-shadow'],
            {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeInOut,
            }
          ),
          // Show drag handle hint on item hover
          '&:hover .sortable-drag-handle': {
            opacity: 0.4,
          },
          // Full opacity when hovering the handle itself
          '&:hover .sortable-drag-handle:hover': {
            opacity: 1,
          },
          // Show on keyboard focus (focus-visible only, not after mouse click)
          '& .sortable-drag-handle:has(:focus-visible)': {
            opacity: 1,
          },
          // Custom styles from props
          ...sx,
        }}
      >
        {/* Drag handle - absolutely positioned, hidden by default */}
        {showDragHandle && (
          <Box
            className="sortable-drag-handle"
            sx={{
              position: 'absolute',
              ...handlePositionStyles,
              opacity: isDragging ? 1 : 0,  // Always visible during drag
              transition: theme.transitions.create(['opacity'], {
                duration: theme.transitions.duration.short,
                easing: theme.transitions.easing.easeInOut,
              }),
              zIndex: 10,
              // Background to make it visible over content
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              borderRadius: 1,
            }}
          >
            <DragHandle
              {...attributes}
              {...listeners}
              disabled={isDisabled}
              data-testid={dragHandleTestId}
            />
          </Box>
        )}

        {/* Content takes full width */}
        <Box sx={{ width: '100%' }}>{children}</Box>
      </Box>
    );
  }
);

SortableListItem.displayName = 'SortableListItem';
